import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ProactivityCompilationProps,
  GetProactivityCompilationDetailedResponse,
  ProactivityItem,
  ProactivityItemActionResponse,
} from '@dreamai/types';
import api from '../../../../api';
import { Spinner } from 'react-bootstrap';
import { ReactComponent as CheckIcon } from '../../../../icons/check.svg';
import { ReactComponent as CloseIcon } from '../../../../icons/close.svg';
import { ReactComponent as ScalesIcon } from '../../../../icons/scales.svg';
import { ReactComponent as DiscussIcon } from '../../../../icons/discuss.svg';
import ErrorState from 'components/common/ErrorState';
import SidewaysLoading from 'components/chat/sidebar/SidewaysLoading';
import { useAgent } from '../../../../contexts/AgentContext';

import './proactivity-compilation.scss';

interface ProactivityCompilationComponentProps
  extends ProactivityCompilationProps {
  onClose?: () => void;
}

const ProactivityCompilation: React.FC<
  ProactivityCompilationComponentProps
> = ({ compilationId, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { selectedAgent } = useAgent();
  const [compilation, setCompilation] =
    useState<GetProactivityCompilationDetailedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingItems, setProcessingItems] = useState<
    Record<string, 'approve' | 'dismiss' | 'do_it' | 'skip'>
  >({});

  const fetchCompilation = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      const response = await api.get<GetProactivityCompilationDetailedResponse>(
        `/proactivity/compilations/${compilationId}/detailed`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setCompilation(response.data);
    } catch (err) {
      console.error('Error fetching compilation:', err);
      setError('Failed to load compilation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompilation();
  }, [compilationId, getAccessTokenSilently]);

  const handleItemAction = async (
    itemId: string,
    action: 'approve' | 'dismiss' | 'do_it' | 'skip',
  ) => {
    setProcessingItems((prev) => ({ ...prev, [itemId]: action }));

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<ProactivityItemActionResponse>(
        `/proactivity/compilations/items/${itemId}/action`,
        { action },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setCompilation((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.map((item) =>
              item.id === itemId
                ? {
                    ...item,
                    status:
                      action === 'dismiss' || action === 'skip'
                        ? 'dismissed'
                        : 'actioned',
                  }
                : item,
            ),
          };
        });
      } else {
        console.error('Action failed:', response.data.error);
      }
    } catch (err) {
      console.error('Error performing action:', err);
    } finally {
      setProcessingItems((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    }
  };

  if (loading) {
    return (
      <div className='proactivity-compilation'>
        <div className='proactivity-compilation-loading'>
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !compilation) {
    return (
      <div className='proactivity-compilation'>
        <div className='proactivity-compilation-error'>
          <ErrorState
            title='An error occurred'
            subtitle='Please refresh and try again'
          />
        </div>
      </div>
    );
  }

  // Group items by type
  const summaries = compilation.items.filter((i) => i.type === 'summary');
  const postChats = compilation.items.filter((i) => i.type === 'post_chat');
  const decisions = compilation.items.filter(
    (i) => i.type === 'user_next_steps' || i.type === 'user_blocking',
  );

  const renderDecisionItem = (item: ProactivityItem) => {
    const isActioned = item.status !== 'pending';
    const isProcessing = !!processingItems[item.id];
    const processingAction = processingItems[item.id];
    const isBlocking = item.type === 'user_blocking';

    const positiveAction = isBlocking ? 'approve' : 'do_it';
    const negativeAction = isBlocking ? 'dismiss' : 'skip';
    const positiveLabel = isBlocking ? 'Approve' : 'Do it';
    const negativeLabel = isBlocking ? 'Dismiss' : 'Skip';
    const doneLabel = isBlocking ? 'Approved' : 'Done';
    const skippedLabel = isBlocking ? 'Dismissed' : 'Skipped';

    return (
      <li
        key={item.id}
        className={`decision-item ${isActioned ? 'actioned' : ''}`}
      >
        <span className='decision-text'>
          {item.nextStepsDescription || item.summaryText}
        </span>
        {!isActioned ? (
          <div className='decision-actions'>
            <button
              className='btn-action positive'
              onClick={() => handleItemAction(item.id, positiveAction)}
              disabled={isProcessing}
            >
              {isProcessing && processingAction === positiveAction ? (
                <SidewaysLoading />
              ) : (
                positiveLabel
              )}
            </button>
            <button
              className='btn-action negative'
              onClick={() => handleItemAction(item.id, negativeAction)}
              disabled={isProcessing}
            >
              {isProcessing && processingAction === negativeAction ? (
                <SidewaysLoading />
              ) : (
                negativeLabel
              )}
            </button>
          </div>
        ) : (
          <span className='decision-status'>
            {item.status === 'actioned' ? (
              <>
                <CheckIcon /> {doneLabel}
              </>
            ) : (
              <>
                <CloseIcon /> {skippedLabel}
              </>
            )}
          </span>
        )}
      </li>
    );
  };

  return (
    <div className='proactivity-compilation'>
      {/* Updates Section */}
      {summaries.length > 0 && (
        <div className='proactivity-section'>
          <h3 className='section-title'>
            {selectedAgent?.dreamieImageUrl && (
              <div className='agent-avatar'>
                <img
                  src={selectedAgent.dreamieImageUrl}
                  alt={selectedAgent.label}
                />
              </div>
            )}
            Updates
          </h3>
          <ul className='content-list'>
            {summaries.map((item) => (
              <li key={item.id}>{item.summaryText}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Decisions Section */}
      {decisions.length > 0 && (
        <div className='proactivity-section'>
          <h3 className='section-title'>
            <ScalesIcon /> Decisions
          </h3>
          <div className='decisions-list'>
            {decisions.map(renderDecisionItem)}
          </div>
        </div>
      )}

      {/* To Discuss Section */}
      {(postChats.length > 0 || compilation.synthesizedPostChats) && (
        <div className='proactivity-section'>
          <h3 className='section-title'>
            <DiscussIcon /> To Discuss
          </h3>
          <div className='to-discuss-wrapper'>
            {compilation.synthesizedPostChats && (
              <p className='agenda-text'>{compilation.synthesizedPostChats}</p>
            )}
            {postChats.length > 0 && (
              <ul className='content-list'>
                {postChats.map((item) => (
                  <li key={item.id}>{item.postChatTopic}</li>
                ))}
              </ul>
            )}
            <button className='btn-discuss'>Let's discuss in chat</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProactivityCompilation;
